import { Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    generateSummarizedTimeSeriesData,
    generateTimeSeriesData,
    generateWeekdayPatternData,
    TimeSeriesDataPoint,
} from '../../../Data/mockDataGenerator';
import BarChartComponent from '../../Charts/Bar';
import ChartSelector from '../../Charts/ChartSelector';
import GaugeChartComponent from '../../Charts/Gauge';
import LineChartComponent from '../../Charts/Line';
import PieChartComponent from '../../Charts/Pie';
import ScatterChartComponent from '../../Charts/Scatter';
import { TimelineOption, TimelineSelector } from '../../Charts/TimelineSelector';
import {
    calculateAverageData,
    calculateGaugeValue,
    convertToScatterData,
    createPieChartData,
    filterSummarizedData,
    filterTimeSeriesData,
    generateTimeLabels,
    getTimeRangeLabel,
    SummarizedDataPoint,
    TimeRange,
} from '../../Charts/utility';
import BasicModal from '../BasicModal';

interface ChartsModalProps {
    availableCharts: string[];
    onClose: () => void;
    open: boolean;
}

function ChartsModal({ availableCharts, onClose, open }: Readonly<ChartsModalProps>) {
    const [selectedChart, setSelectedChart] = useState<string>('Bar');
    const [selectedTimeline, setSelectedTimeline] = useState<TimelineOption>('week');

    // States for the pre-generated data
    const [allTimeSeriesData, setAllTimeSeriesData] = useState<TimeSeriesDataPoint[]>([]);
    const [allSummarizedData, setAllSummarizedData] = useState<SummarizedDataPoint[]>([]);
    const [allWeekdayPatternData, setAllWeekdayPatternData] = useState<TimeSeriesDataPoint[]>([]);
    const [dataInitialized, setDataInitialized] = useState(false);

    // Generate all data once when component mounts or when charts change
    useEffect(() => {
        if (!dataInitialized && availableCharts.length > 0) {
            // Generate a full year of data at once
            const maxDays = 365;

            // Generate detailed time series data (for shorter periods)
            if (availableCharts.some((chart) => ['Bar', 'Line', 'Scatter', 'Pie', 'Gauge'].includes(chart))) {
                const timeSeriesData = generateTimeSeriesData(maxDays, 24, 10, 100, 'random');
                setAllTimeSeriesData(timeSeriesData);
            }

            // Generate summarized data (for longer periods)
            if (availableCharts.some((chart) => ['Bar', 'Line', 'Pie', 'Gauge'].includes(chart))) {
                const summarizedData = generateSummarizedTimeSeriesData(maxDays, 10, 100, 'random');
                setAllSummarizedData(summarizedData);
            }

            // Generate weekday pattern data (for Scatter chart)
            if (availableCharts.includes('Scatter')) {
                const weekdayData = generateWeekdayPatternData(maxDays, 12, [50, 100], [20, 60]);
                setAllWeekdayPatternData(weekdayData);
            }

            setDataInitialized(true);
        }
    }, [availableCharts, dataInitialized]);

    // Define time range based on selected timeline
    const timeRange = useMemo((): TimeRange => {
        switch (selectedTimeline) {
            case 'today':
                return {
                    days: 1,
                    format: 'HH:mm',
                    hours: 24,
                    isHourly: true,
                };
            case '3days':
                return { days: 3, format: 'DD/MM', hours: 0, isHourly: false };
            case 'week':
                return { days: 7, format: 'DD/MM', hours: 0, isHourly: false };
            case 'month':
                return { days: 30, format: 'DD/MM', hours: 0, isHourly: false };
            case '3months':
                return { days: 90, format: 'DD/MM', hours: 0, isHourly: false };
            case '6months':
                return { days: 180, format: 'DD/MM', hours: 0, isHourly: false };
            case 'year':
                return { days: 365, format: 'MM/YYYY', hours: 0, isHourly: false };
            default:
                return { days: 7, format: 'DD/MM', hours: 0, isHourly: false };
        }
    }, [selectedTimeline]);

    // Determine if we should use summarized data for longer periods
    const useSummarizedData = useMemo(() => {
        return timeRange.days > 60 || selectedTimeline === 'year' || selectedTimeline === '6months';
    }, [timeRange.days, selectedTimeline]);

    // Filter the data based on the selected time range
    const timeSeriesData = useMemo(() => {
        if (useSummarizedData || !dataInitialized) {
            return [];
        }
        return filterTimeSeriesData(allTimeSeriesData, timeRange);
    }, [allTimeSeriesData, timeRange, useSummarizedData, dataInitialized]);

    // Filter summarized data based on the selected time range
    const summarizedTimeSeriesData = useMemo(() => {
        if (!useSummarizedData || !dataInitialized) {
            return [];
        }
        return filterSummarizedData(allSummarizedData, timeRange);
    }, [allSummarizedData, timeRange, useSummarizedData, dataInitialized]);

    // Filter weekday pattern data for scatter plot
    const weekdayPatternData = useMemo(() => {
        if (selectedChart !== 'Scatter' || !dataInitialized) {
            return [];
        }
        return filterTimeSeriesData(allWeekdayPatternData, timeRange);
    }, [allWeekdayPatternData, selectedChart, timeRange, dataInitialized]);

    // Generate data for bar and line charts
    const barData = useMemo(() => {
        if ((selectedChart !== 'Bar' && selectedChart !== 'Line') || !dataInitialized) {
            return [];
        }
        return calculateAverageData(timeSeriesData, summarizedTimeSeriesData, timeRange, useSummarizedData);
    }, [timeSeriesData, summarizedTimeSeriesData, timeRange, selectedChart, useSummarizedData, dataInitialized]);

    // Generate x-axis labels
    const chartLabels = useMemo(() => {
        if ((selectedChart !== 'Bar' && selectedChart !== 'Line') || !dataInitialized) {
            return [];
        }
        return generateTimeLabels(timeRange, useSummarizedData, summarizedTimeSeriesData);
    }, [timeRange, selectedChart, summarizedTimeSeriesData, useSummarizedData, dataInitialized]);

    // Generate pie chart data
    const pieData = useMemo(() => {
        if (selectedChart !== 'Pie' || !dataInitialized) {
            return [];
        }
        return createPieChartData(timeSeriesData, summarizedTimeSeriesData, useSummarizedData);
    }, [timeSeriesData, summarizedTimeSeriesData, selectedChart, useSummarizedData, dataInitialized]);

    // Generate scatter plot data
    const scatterData = useMemo(() => {
        if (selectedChart !== 'Scatter' || !dataInitialized) {
            return [];
        }
        return convertToScatterData(timeSeriesData, summarizedTimeSeriesData, weekdayPatternData, useSummarizedData);
    }, [
        timeSeriesData,
        summarizedTimeSeriesData,
        weekdayPatternData,
        selectedChart,
        useSummarizedData,
        dataInitialized,
    ]);

    // Calculate gauge value
    const gaugeValue = useMemo(() => {
        if (selectedChart !== 'Gauge' || !dataInitialized) {
            return 0;
        }
        return calculateGaugeValue(timeSeriesData, summarizedTimeSeriesData, useSummarizedData);
    }, [timeSeriesData, summarizedTimeSeriesData, selectedChart, useSummarizedData, dataInitialized]);

    // Handle chart selection
    const handleChartSelection = useCallback((chart: string) => {
        setSelectedChart(chart);
    }, []);

    // Render the currently selected chart
    const renderSelectedChart = useCallback(() => {
        if (!dataInitialized) {
            return <div>Loading data...</div>;
        }

        const timeRangeText = getTimeRangeLabel(selectedTimeline);

        switch (selectedChart) {
            case 'Bar':
                return (
                    <BarChartComponent
                        data={barData}
                        labels={chartLabels}
                        title={`Average values (${timeRangeText})`}
                    />
                );
            case 'Line':
                return (
                    <LineChartComponent
                        data={barData}
                        labels={chartLabels}
                        showMarkers={['today', '3days', 'week'].includes(selectedTimeline)}
                        title={`Trend (${timeRangeText})`}
                    />
                );
            case 'Pie':
                return <PieChartComponent data={pieData} title={`Distribution by value range (${timeRangeText})`} />;
            case 'Scatter':
                return (
                    <ScatterChartComponent
                        data={scatterData}
                        title={`Correlation between datasets (${timeRangeText})`}
                    />
                );
            case 'Gauge':
                return <GaugeChartComponent title={`Average value (${timeRangeText})`} value={gaugeValue} />;
            default:
                return null;
        }
    }, [dataInitialized, selectedChart, selectedTimeline, barData, chartLabels, pieData, scatterData, gaugeValue]);

    return (
        <BasicModal handleClose={onClose} open={open} title="Charts">
            <Stack
                sx={{
                    backgroundColor: '#edf2f6',
                    display: 'flex',
                    flexDirection: 'column',
                    height: { md: 600, xs: 'calc(100dvh - 60px)' },
                    p: 2,
                    width: { md: 850, xs: '100dvw' },
                }}
            >
                <ChartSelector
                    availableCharts={availableCharts}
                    onSelectChart={handleChartSelection}
                    selectedChart={selectedChart}
                />

                {renderSelectedChart()}

                <TimelineSelector selectedTimeline={selectedTimeline} setSelectedTimeline={setSelectedTimeline} />
            </Stack>
        </BasicModal>
    );
}

export default ChartsModal;
